// router/index.js
import Vue from 'vue';
import VueRouter from 'vue-router';
import HomeView from '../views/HomeView.vue';

Vue.use(VueRouter);

const routes = [
  {
    path: '/login',
    name: 'Login page',
    component: () => import('../views/Login.vue'),
    meta: { title: 'Login - SIDAR', isPublic: true, showInHome: false },
  },
  {
    path: '/',
    name: 'Home',
    component: HomeView,
    meta: { title: 'SIDAR', allowOffline: true },
  },
  {
    path: '/addresses',
    name: 'Address Management',
    component: () => import('../views/Addresses.vue'),
    meta: { title: 'Manage addresses', icon: 'mdi-file-sign', requiresAuth: true, showInHome: true, homeOrder: 1 },
  },
  {
    path: '/import',
    name: 'Import',
    component: () => import('../views/Import.vue'),
    meta: {
      title: 'Import data',
      icon: 'mdi-import',
      roles: ['Admin', 'Staff members', 'Partner organization manager'],
      disableMobile: true,
      requiresAuth: true,
      allowOffline: false,
      showInHome: true,
      homeOrder: 3,
    },
  },
  {
    path: '/report',
    name: 'Report',
    component: () => import('../views/Report.vue'),
    meta: { title: 'Reports', icon: 'mdi-chart-box-outline', requiresAuth: true, showInHome: true, homeOrder: 2 },
  },
  {
    path: '/organizations',
    name: 'Manage Organizations',
    component: () => import('../views/Organizations.vue'),
    meta: { title: 'Organizations', icon: 'mdi-domain', requiresAuth: true, roles: ['Admin', 'Staff members'], showInHome: true, homeOrder: 4 },
  },
  {
    path: '/metadata',
    name: 'Metadata Settings',
    component: () => import('../views/Metadata.vue'),
    meta: { title: 'Custom data settings', icon: 'mdi-script-text-outline', requiresAuth: true, roles: ['Admin', 'Staff members', 'Partner organization manager'], showInHome: true, homeOrder: 5 },
  },
  {
    path: '/users',
    name: 'Manage Users',
    component: () => import('../views/Users.vue'),
    meta: {
      title: 'Users', // title for the page
      icon: 'mdi-account-multiple-outline',
      requiresAuth: true,
      showInHome: true,
      homeOrder: 6,
      // roles: ['Admin', 'Staff members', 'Partner organization manager', 'Partner organization members'],
    },
  },
  // {
  //   path: '/addressesTest',
  //   name: 'Address Management Test',
  //   component: () => import('../views/AddressesNew.vue'),
  //   meta: { title: 'Address Management', requiresAuth: true, icon: 'mdi-file-sign', showInHome: false },
  // },
  {
    path: '/address/:_id',
    name: 'Address Review',
    component: () => import('../views/Address.vue'),
    meta: { title: 'Address - SIDAR', requiresAuth: true, showInHome: false },
  },
  {
    path: '/address/:_id/apartment/:apartmentId',
    name: 'Residential Unit Review',
    component: () => import('../views/AddressNew.vue'),
    meta: { title: 'Residential Unit - SIDAR', requiresAuth: true, showInHome: false },
  },
  {
    path: '/user',
    name: 'User',
    component: () => import('../views/User.vue'),
    meta: { title: 'User - SIDAR', requiresAuth: true, showInHome: false },
  },
  {
    path: '/guide',
    name: 'Guide',
    component: () => import('../views/Guide.vue'),
    meta: { title: 'User guide - SIDAR', requiresAuth: true, showInHome: false },
  },
  {
    path: '/about',
    name: 'About',
    component: () => import('../views/About.vue'),
    meta: { title: 'About - SIDAR', requiresAuth: true, showInHome: false },
  },
  {
    path: '/updates',
    name: 'Updates',
    component: () => import('../views/Updates.vue'),
    meta: { title: 'Updates - SIDAR', requiresAuth: true, showInHome: false },
  },
  // {
  //   path: '/activities',
  //   name: 'Activities',
  //   component: () => import('../views/Activities.vue'),
  //   meta: { title: 'Activities - SIDAR', requiresAuth: true, showInHome: false },
  // },
  // {
  //   path: '/test',
  //   name: 'Test',
  //   component: () => import('../views/Test.vue'),
  //   meta: { title: 'About - SIDAR', requiresAuth: true, showInHome: false },
  // },
  {
    path: '*',
    beforeEnter: (to, from, next) => {
      const isOffline = navigator.onLine === false;
      if (isOffline) {
        next('/');
      } else {
        next({ path: '/error/404' });
      }
    },
  },
  {
    path: '/error/:code',
    name: 'ErrorPage',
    component: () => import('../views/ErrorPage.vue'),
    meta: { title: 'Not Found - SIDAR', isPublic: true, showInHome: false },
  },
];

const router = new VueRouter({
  mode: 'history',
  routes,
  scrollBehavior() {
    return { x: 0, y: 0 };
  },
});

// Simplified and more reliable navigation guard
router.beforeEach((to, from, next) => {
  // Set page title
  const title = to.meta?.title || 'SIDAR';
  document.title = title;

  // Handle meta tags
  handleMetaTags(to, from);

  // Use setTimeout to ensure store is available
  setTimeout(() => {
    const store = router.app?.$store;
    const isOnline = store?.state?.system?.isOnline ?? true;
    const isMobile = store?.state?.system?.isMobile ?? false;
    const user = store?.state?.user ?? {};

    // Check offline access
    if (!isOnline && !to.meta.allowOffline) {
      return next('/');
    }

    // Check mobile restrictions
    if (to.meta.disableMobile && isMobile && from.path !== '/') {
      return next('/');
    }

    // Public routes (login, register, etc.)
    if (to.meta.isPublic || to.path === '/login' || to.path === '/register') {
      // If user is already authenticated and trying to access login
      if (user?.name && to.path === '/login') {
        return next('/');
      }
      return next();
    }

    // Protected routes
    if (to.meta.requiresAuth) {
      // Check if user is authenticated
      if (!user?.name) {
        return next('/login');
      }

      // Check role-based access
      if (to.meta.roles && user?.role !== 'Admin') {
        if (!to.meta.roles.includes(user?.role)) {
          return next('/'); // or a 403 page
        }
      }
    }

    // All checks passed
    next();
  }, 0);
});

function handleMetaTags(to, from) {
  const nearestWithTitle = to.matched
    .slice()
    .reverse()
    .find((r) => r.meta && r.meta.title);

  const nearestWithMeta = to.matched
    .slice()
    .reverse()
    .find((r) => r.meta && r.meta.metaTags);

  const previousNearestWithMeta = from.matched
    .slice()
    .reverse()
    .find((r) => r.meta && r.meta.metaTags);

  if (nearestWithTitle) {
    document.title = nearestWithTitle.meta.title;
  } else if (previousNearestWithMeta) {
    document.title = previousNearestWithMeta.meta.title;
  }

  // Remove existing meta tags
  Array.from(document.querySelectorAll('[data-vue-router-controlled]')).forEach((el) => el.parentNode.removeChild(el));

  if (!nearestWithMeta) return;

  // Add new meta tags
  nearestWithMeta.meta.metaTags
    .map((tagDef) => {
      const tag = document.createElement('meta');
      Object.keys(tagDef).forEach((key) => {
        tag.setAttribute(key, tagDef[key]);
      });
      tag.setAttribute('data-vue-router-controlled', '');
      return tag;
    })
    .forEach((tag) => document.head.appendChild(tag));
}

export default router;
